import { useCallback, useEffect, useRef, useState } from "react";
import type { CreateGameFormProps } from "../../game-registry";

export type FirstPlayerPolicy = "random" | "host" | "guest";

const FIRST_PLAYER_OPTIONS: readonly {
  readonly value: FirstPlayerPolicy;
  readonly label: string;
}[] = [
  { value: "random", label: "무작위" },
  { value: "host", label: "방장 선공" },
  { value: "guest", label: "참가자 선공" },
];

export function BlackAndWhiteCreateGameForm({
  onConfigChange,
}: CreateGameFormProps) {
  const [firstPlayerPolicy, setFirstPlayerPolicy] =
    useState<FirstPlayerPolicy>("random");
  const [observerGodViewEnabled, setObserverGodViewEnabled] = useState(false);
  const onConfigChangeRef = useRef(onConfigChange);
  onConfigChangeRef.current = onConfigChange;

  useEffect(() => {
    onConfigChangeRef.current({
      firstPlayerPolicy: "random",
      observerGodViewEnabled: false,
    });
  }, []);

  const handleFirstPlayerChange = useCallback(
    (policy: FirstPlayerPolicy) => {
      setFirstPlayerPolicy(policy);
      onConfigChange({
        firstPlayerPolicy: policy,
        observerGodViewEnabled,
      });
    },
    [observerGodViewEnabled, onConfigChange],
  );

  const handleObserverGodViewChange = useCallback(
    (enabled: boolean) => {
      setObserverGodViewEnabled(enabled);
      onConfigChange({
        firstPlayerPolicy,
        observerGodViewEnabled: enabled,
      });
    },
    [firstPlayerPolicy, onConfigChange],
  );

  return (
    <div className="flex flex-col gap-3 rounded-md border border-border-subtle bg-surface-secondary p-3">
      <div>
        <div className="mb-2 font-semibold text-sm text-text-primary">
          선공 결정
        </div>
        <div className="grid grid-cols-3 gap-2">
          {FIRST_PLAYER_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              data-testid={`black-and-white-first-player-${option.value}`}
              className={`rounded-md border px-3 py-2 font-semibold text-xs transition-colors ${
                firstPlayerPolicy === option.value
                  ? "border-text-primary bg-text-primary text-white"
                  : "border-border-primary bg-surface-primary text-text-secondary"
              }`}
              onClick={() => handleFirstPlayerChange(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <label className="flex items-start gap-3 rounded-md border border-border-primary bg-surface-primary px-3 py-2 text-sm">
        <input
          type="checkbox"
          data-testid="black-and-white-observer-god-view-toggle"
          checked={observerGodViewEnabled}
          onChange={(event) =>
            handleObserverGodViewChange(event.target.checked)
          }
          className="mt-1"
        />
        <span className="flex-1">
          <span className="block font-semibold text-text-primary">
            관전자 전체 손패 허용
          </span>
          <span className="mt-1 block text-text-muted text-xs">
            허용된 관전자는 양쪽 손패와 제출 숫자를 항상 볼 수 있습니다.
          </span>
        </span>
      </label>
    </div>
  );
}
